import { put, takeEvery, select } from 'redux-saga/effects'

import firebase from 'firebase/app'

import { fetchFirebaseTokenAction, setTokenAction } from '@redux/actions/auth'

import * as Sentry from '@sentry/browser'

import {
  AUTH_FETCH_FIREBASE_TOKEN_SUCCESS,
  AUTH_SET_KEYS,
  AUTH_VALIDATE_SUCCESS,
  SAFEWALK_SET_DATA,
  SET_USER_CONFIG
} from '@redux/actions/types'

import dayjs from 'dayjs'

const getAnalytics = state => state.analytic.analytics
const getSafeWalkData = state => state.safewalk
const getAuth = state => ({
  hash: state.auth.hash
})

/**
 * Fetches Firebase token and sets auth token when API key and hash are available.
 * @param action
 */
function * onSetKeys(action) {
  const {
    payload: {
      apiKey,
      hash
    }
  } = action
  // Potential dead end, investigate this if we have instances of a spinner without and error.
  if (apiKey && hash) {
    yield put(fetchFirebaseTokenAction({ apiKey, hash }))
    yield put(setTokenAction({ apiKey, hash }))
  }
}

/**
 * Signs in to Firebase with the custom token received from the backend.
 * @param action
 */
function * onTokenSuccess(action) {
  const { token } = action.payload.data

  const { hash } = yield select(getAuth)

  firebase.auth().signInWithCustomToken(token).catch(error => {
    Sentry.captureException(error, (scope) => {
      scope.setExtra('access_code', hash)
    })
  })
}

/**
 * Sets analytics user properties and dispatches auth keys after validation succeeds.
 * @param action
 */
function * onValidateSuccess(action) {
  const Analytics = yield select(getAnalytics)
  const {
    payload: {
      data: {
        account_id: accountId,
        api_key: apiKey,
        first_name: firstName,
        account_hash: accountHash,
        hash,
        last_name: lastName,
        subscriber_id: subscriberId,
        username
      }
    }
  } = action

  const user = { accountId, apiKey, firstName, hash, lastName, subscriberId, username }

  Analytics.setUserId(hash)
  Analytics.setUserProperty(user)

  yield put({
    type: AUTH_SET_KEYS,
    payload: { accountId, accountHash, apiKey, hash, subscriberId }
  })
}

/**
 * Processes user and account config to update SafeWalk display state and off-hours status.
 * @param root0
 * @param root0.payload
 */
function * onSetUserConfig({ payload }) {
  const { accountConfig, userConfig } = payload

  const safeWalkConfig = accountConfig?.safe_walk
  const safeWalkData = userConfig?.safe_walk

  const { displayStates, nextState } = yield select(getSafeWalkData)

  const { state } = { ...safeWalkData }

  const activeDisplay = displayStates.find(({ state: st }) => {
    return nextState === 'warning' ? st === 'warning' : st === state
  }) || null

  yield put({
    type: SAFEWALK_SET_DATA,
    payload: { activeDisplay }
  })

  if (safeWalkConfig) {
    const { hours_of_operation: hoursOfOperation } = safeWalkConfig
    const { end_time: endTime, start_time: startTime } = hoursOfOperation

    const formatStartTime = dayjs().hour(startTime.hour).minute(startTime.min)
    const formatEndTime = dayjs().hour(endTime.hour).minute(endTime.min)

    const isOffHours = !dayjs().isBetween(formatStartTime, dayjs(formatEndTime), 'minutes')

    yield put({
      type: SAFEWALK_SET_DATA,
      payload: {
        isOffHours
      }
    })
  }
}

/**
 * Registers watchers for auth-related actions.
 */
function * watchAuth() {
  yield takeEvery(AUTH_SET_KEYS, onSetKeys)
  yield takeEvery(AUTH_FETCH_FIREBASE_TOKEN_SUCCESS, onTokenSuccess)
  yield takeEvery(AUTH_VALIDATE_SUCCESS, onValidateSuccess)
  yield takeEvery(SET_USER_CONFIG, onSetUserConfig)
}

export default watchAuth
